import * as React from "react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { useAuthReady } from "../AuthProvider";
import { useAuth } from "../../../lib/auth/hooks";
import { AuthenticatedLayout } from "../AuthenticatedLayout";
import { getUserSetupRedirectSearch } from "../../../lib/auth/user-setup-search";
import {
  bounceToAccountLogin,
  bounceToAccountPath,
  getAccountAppOrigin,
  hardNavigateOnce,
} from "../../../lib/auth/app-origins";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";

/** Auth paths that now live on account-web — permanently redirect bookmarks. */
const ACCOUNT_AUTH_PATHS = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/user-setup",
  "/verify-email",
  "/accept-invite",
  "/auth/google/callback",
]);

const PUBLIC_PATHS = new Set([
  "/health",
  "/captured-emails",
  "/captured-sms",
]);

export function AppShell() {
  const { isAuthenticated, user } = useAuth();
  const authReady = useAuthReady();
  const location = useLocation();
  const pathname = location.pathname;
  // Prevent effect re-runs (authReady → user hydrate) from spamming location.replace.
  const bounceStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (!authReady) return;
    if (bounceStartedRef.current) return;
    if (pathname === "/redirecting") return;

    // Old pro-web auth URLs → account-web (preserve path + query).
    // Route beforeLoad also bounces; this covers deep links that skip beforeLoad timing.
    if (ACCOUNT_AUTH_PATHS.has(pathname) || pathname.startsWith("/auth/google")) {
      bounceStartedRef.current = true;
      bounceToAccountPath(pathname, window.location.search);
      return;
    }

    if (
      isAuthenticated &&
      user &&
      !user.profileSetupCompleted &&
      !PUBLIC_PATHS.has(pathname)
    ) {
      bounceStartedRef.current = true;
      const search = getUserSetupRedirectSearch(user);
      const params = new URLSearchParams();
      if (search.tab) params.set("tab", search.tab);
      if (search.email) params.set("email", search.email);
      if (search.code) params.set("code", search.code);
      const qs = params.toString();
      hardNavigateOnce(`${getAccountAppOrigin()}/user-setup${qs ? `?${qs}` : ""}`);
      return;
    }

    if (!isAuthenticated && !PUBLIC_PATHS.has(pathname)) {
      bounceStartedRef.current = true;
      bounceToAccountLogin(`${window.location.origin}${pathname}${window.location.search}`);
    }
  }, [authReady, isAuthenticated, user, pathname]);

  if (!authReady) {
    return <AuthTransitionScreen message="Loading…" />;
  }

  if (
    pathname === "/redirecting" ||
    ACCOUNT_AUTH_PATHS.has(pathname) ||
    pathname.startsWith("/auth/google")
  ) {
    return <AuthTransitionScreen message="Redirecting to sign in…" />;
  }

  if (
    pathname === "/health" ||
    pathname === "/captured-emails" ||
    pathname === "/captured-sms"
  ) {
    return <Outlet />;
  }

  if (isAuthenticated) {
    if (user && !user.profileSetupCompleted) {
      return <AuthTransitionScreen message="Redirecting…" />;
    }
    return (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    );
  }

  return <AuthTransitionScreen message="Redirecting to sign in…" />;
}
